import React from 'react';
import { ArrowUpIcons } from '../../../../icons/icons';
import { LoadingOverlay } from './Conmponents/LoadingOverlay';
import Pagination from './Paginaion';

export interface TableColumn<T> {
  key: keyof T | string;
  label: string;
  width?: string;
  align?: 'left' | 'center' | 'right';
  render?: (row: T) => JSX.Element;
  disableSort?: boolean;
  sortValue?: (row: T) => number | string; // Function to extract sort value
}

interface SortConfig {
  key: string;
  direction: 'asc' | 'desc';
}

interface DataTableProps<T> {
  isLoading?: boolean;
  columns?: TableColumn<T>[];
  data?: T[];
  rowsPerPage: number;
  onClickRow?: (row: T) => void;
  sortConfig?: SortConfig[];
  onSortChange?: (key: string, direction: 'asc' | 'desc') => void;
  currentPage: number;
  totalRecords: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  hasCheckBox?: boolean;
  hidePagination?: boolean;
  onSelectAll?: () => void;
  onSelectRow?: (rowIndex: number, row: T) => void;
  isRowSelected?: (rowIndex: number, row: T) => boolean;
  isAllSelected?: boolean;
  isIndeterminate?: boolean;
  hasHidePagination?: boolean;
  minTableHeight?: string;
}

export const DataTable = <T,>({
  columns = [],
  data = [],
  rowsPerPage = 5,
  onClickRow,
  isLoading,
  sortConfig = [],
  onSortChange,
  currentPage,
  totalPages,
  totalRecords,
  onPageChange,
  hasCheckBox,
  hidePagination,
  onSelectAll,
  onSelectRow,
  isRowSelected,
  isAllSelected = false,
  isIndeterminate = false,
  hasHidePagination,
  minTableHeight = 'auto',
}: DataTableProps<T>) => {
  const minimumTableWidth = data.length === 0 ? 0 : Math.max(
    columns.length * 112 + (hasCheckBox ? 52 : 0),
    520,
  );

  const handleSort = (key: string) => {
    if (!onSortChange || columns.find((col) => col.key === key)?.disableSort)
      return;

    const existingSortIndex = sortConfig.findIndex((sort) => sort.key === key);
    const newSortConfig = [...sortConfig];

    if (existingSortIndex !== -1) {
      const currentSort = newSortConfig[existingSortIndex];
      const newDirection = currentSort.direction === 'asc' ? 'desc' : 'asc';
      newSortConfig[existingSortIndex] = { key, direction: newDirection };
    } else {
      newSortConfig.push({ key, direction: 'asc' });
    }

    const { direction } =
      newSortConfig[
        existingSortIndex !== -1 ? existingSortIndex : newSortConfig.length - 1
      ];

    onSortChange(key, direction);
  };

  return (
    <div className='flex flex-col h-full w-full min-w-0 rounded-lg bg-white'>
      { /* Scrollable table container - header scrolls horizontally with body */ }
      <div
          className='flex-1 min-h-0 overflow-auto w-full border border-slate-200 rounded-lg overscroll-contain'
          style={{ minHeight: minTableHeight && minTableHeight !== 'auto' ? minTableHeight : undefined }}
      >
        <table
            className='w-full border-collapse text-left'
            style={{ minWidth: minimumTableWidth || '100%' }}
        >
          <thead className='sticky top-0 bg-slate-50/90 backdrop-blur-md z-10 border-b border-slate-200/70'>
            <tr>
              { hasCheckBox && (
                <th
                    className='px-4 py-3.5 text-left text-[11px] font-semibold text-slate-400 uppercase tracking-wider bg-inherit'
                    style={{ width: '48px', minWidth: '48px' }}
                >
                  <input
                      type='checkbox'
                      checked={isAllSelected}
                      ref={(el) => {
                        if (el) el.indeterminate = isIndeterminate;
                      }}
                      onChange={onSelectAll}
                      className='rounded-md border-slate-300 text-emerald-600 focus:ring-emerald-500'
                  />
                </th>
              ) }
              { columns.map((col, index) => {
                const columnSort = sortConfig?.find(
                  (s) => s.key === String(col.key),
                );
                const isAsc = columnSort?.direction === 'asc';
                return (
                  <th
                      key={index}
                      className={`px-4 py-3.5 text-[11px] font-semibold text-slate-400 uppercase tracking-wider bg-inherit whitespace-nowrap ${
                        col.align === 'center'
                          ? '!text-center'
                          : col.align === 'right'
                            ? '!text-right'
                            : '!text-left'
                      }`}
                      style={{
                        width: col.width || 'auto',
                        minWidth: col.width || 'auto',
                      }}
                  >
                    <div
                        className={`flex items-center gap-2 ${
                          !col.disableSort ? 'cursor-pointer group' : ''
                        } ${
                          col.align === 'center'
                            ? 'justify-center'
                            : col.align === 'right'
                              ? 'justify-end'
                              : 'justify-start !text-left'
                        }`}
                        onClick={() =>
                          !col.disableSort && handleSort(String(col.key))
                        }
                    >
                      <div className='relative group/tooltip max-w-full flex-1 min-w-0 !text-left'>
                        <span className='truncate block !text-left font-semibold'>{ col.label }</span>
                        { col.label && col.label.length > 20 && (
                          <div className='absolute z-[9999] opacity-0 group-hover/tooltip:opacity-100 transition-opacity duration-200 pointer-events-none left-0 top-full mt-1'>
                            <div className='bg-slate-900 text-white text-[10px] font-medium rounded-lg px-2.5 py-1.5 shadow-xl whitespace-nowrap backdrop-blur-md'>
                              { col.label }
                              <div className='absolute bottom-full left-4 w-0 h-0 border-l-4 border-r-4 border-b-4 border-transparent border-b-slate-900'></div>
                            </div>
                          </div>
                        ) }
                      </div>
                      { !col.disableSort && (
                        <div
                            className={`
                            flex flex-row items-center shrink-0
                            ${
                              columnSort
                                ? 'opacity-100'
                                : 'opacity-0 group-hover:opacity-100 transition-opacity duration-200'
                            }
                          `}
                        >
                          { columnSort ? (
                            // Showing active sort direction only
                            isAsc ? (
                              <ArrowUpIcons className='w-4 h-4 text-emerald-600 stroke-[3]' />
                            ) : (
                              <ArrowUpIcons className='w-4 h-4 text-emerald-600 transform rotate-180 stroke-[3]' />
                            )
                          ) : (
                            // Default state (unsorted) - show both distinct but subtle
                            <div className='flex flex-col gap-[1px]'>
                              <ArrowUpIcons className='w-2.5 h-2.5 text-slate-400' />
                              <ArrowUpIcons className='w-2.5 h-2.5 text-slate-400 transform rotate-180' />
                            </div>
                          ) }
                        </div>
                      ) }
                    </div>
                  </th>
                );
              }) }
            </tr>
          </thead>
          <tbody className='bg-white divide-y divide-slate-100'>
            { isLoading && data.length === 0 ? (
              <tr>
                <td className='px-4 py-12 text-center text-slate-500'>
                  <LoadingOverlay />
                </td>
              </tr>
            ) : data.length === 0 ? (
              <tr>
                <td
                    colSpan={columns.length + (hasCheckBox ? 1 : 0)}
                    className='px-4 py-8 text-center text-slate-500'
                >
                  <div className='flex items-center justify-center py-6'>
                    <div className='flex max-w-[360px] items-center gap-3 rounded-xl border border-dashed border-slate-200 bg-slate-50/70 px-4 py-3 text-left'>
                      <svg
                          className='h-8 w-8 shrink-0 text-slate-300'
                          fill='none'
                          viewBox='0 0 24 24'
                          stroke='currentColor'
                      >
                        <path
                            strokeLinecap='round'
                            strokeLinejoin='round'
                            strokeWidth={1.5}
                            d='M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4'
                        />
                      </svg>
                      <div>
                        <h3 className='text-sm font-semibold tracking-tight text-slate-800'>
                          No Data Available
                        </h3>
                        <p className='mt-0.5 text-xs text-slate-500'>
                          There are no records to display in this category at the
                          moment.
                        </p>
                      </div>
                    </div>
                  </div>
                </td>
              </tr>
            ) : (
              data.map((row, rowIndex) => (
                <tr
                    key={rowIndex}
                    onClick={() => onClickRow?.(row)}
                    className={`${
                      onClickRow ? 'cursor-pointer hover:bg-slate-50/70' : ''
                    } ${
                      isRowSelected?.(rowIndex, row) ? 'bg-emerald-50/50' : ''
                    } transition-colors duration-150`}
                >
                  { hasCheckBox && (
                    <td className='px-4 py-3.5 whitespace-nowrap'>
                      <input
                          type='checkbox'
                          checked={isRowSelected?.(rowIndex, row) || false}
                          onChange={(e) => {
                            e.stopPropagation();
                            onSelectRow?.(rowIndex, row);
                          }}
                          onClick={(e) => e.stopPropagation()}
                          className='rounded-md border-slate-300 text-emerald-600 focus:ring-emerald-500'
                      />
                    </td>
                  ) }
                  { columns.map((column, colIndex) => (
                    <td
                        key={colIndex}
                        className={`px-4 py-3.5 text-[13px] leading-5 text-slate-700 whitespace-nowrap ${
                          column.align === 'center'
                            ? 'text-center'
                            : column.align === 'right'
                              ? 'text-right'
                              : 'text-left'
                        }`}
                        style={{
                          width: column.width || 'auto',
                        }}
                        data-label={column.label}
                    >
                      { column.render
                        ? column.render(row)
                        : (row[column.key as keyof T] as React.ReactNode) }
                    </td>
                  )) }
                </tr>
              ))
            ) }
          </tbody>
        </table>
        { /* Loading overlay for when data is being refreshed */ }
        { isLoading && data.length > 0 && <LoadingOverlay /> }
      </div>

      { /* Fixed footer - pagination */ }
      { !hidePagination && !isLoading && data.length > 0 && (
        <div className='shrink-0 border-t border-slate-100 bg-slate-50/40 rounded-b-2xl'>
          <Pagination
              totalRecords={totalRecords}
              currentPage={currentPage}
              handlePageChange={onPageChange}
              totalPages={totalPages}
              rowsPerPage={rowsPerPage}
              hasHidePagination={hasHidePagination}
          />
        </div>
      ) }
    </div>
  );
};
